import { useParams } from "react-router-dom";
import { useFilters } from "../context/FilterContext";
import { useFetch } from "../lib/useFetch";
import { PageHeader, Panel, Stat, Loading, Empty } from "../components/common/ui";
import { SubTabs } from "../components/common/SubTabs";
import { AreaTrend, MultiLine, Bars, Donut } from "../components/common/Charts";
import { fmt, inr } from "../lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const TABS = [
  { key: "revenue", label: "Revenue" }, { key: "orders", label: "Orders" },
  { key: "services", label: "Services" }, { key: "customers", label: "Customers" },
  { key: "growth", label: "Growth" },
];

export default function BusinessIntelligence() {
  const { tab = "revenue" } = useParams();
  const { range, hub } = useFilters();
  const q = `?range=${range}&hub=${hub}`;
  return (
    <div data-testid="bi-page">
      <PageHeader title="Business Intelligence" subtitle="Revenue, orders, services and growth analytics across the business." />
      <SubTabs base="/bi" tab={tab} items={TABS} />
      {tab === "revenue" && <Revenue q={q} deps={[range, hub]} />}
      {tab === "orders" && <Orders q={q} deps={[range, hub]} />}
      {tab === "services" && <Services q={q} deps={[range, hub]} />}
      {tab === "customers" && <CustomersBI />}
      {tab === "growth" && <Growth hub={hub} />}
    </div>
  );
}

function Revenue({ q, deps }) {
  const { data, loading } = useFetch(`/bi/revenue${q}`, deps);
  if (loading || !data) return <Loading rows={4} />;
  const t = data.totals;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Gross Revenue" value={"₹" + inr(t.gross)} />
        <Stat label="Net Revenue" value={"₹" + inr(t.net)} tone="text-primary" />
        <Stat label="Discounts" value={"₹" + inr(t.discounts)} />
        <Stat label="Refunds" value={"₹" + inr(t.refunds)} />
        <Stat label="Taxes" value={"₹" + inr(t.taxes)} />
        <Stat label="Growth" value={`${data.change_pct}%`} tone={data.change_pct >= 0 ? "text-emerald-600" : "text-red-600"} />
      </div>
      <Panel title="Revenue Trend"><AreaTrend data={data.trend} currency height={280} /></Panel>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Revenue by Hub"><Bars data={data.by_hub} horizontal currency height={300} /></Panel>
        <Panel title="Revenue by Service"><Donut data={data.by_service} currency height={300} /></Panel>
        <Panel title="Revenue by Channel"><Donut data={data.by_channel} currency height={280} /></Panel>
        <Panel title="Revenue by Customer Segment"><Bars data={data.by_segment} horizontal currency height={280} /></Panel>
      </div>
    </div>
  );
}

function Orders({ q, deps }) {
  const { data, loading } = useFetch(`/bi/orders${q}`, deps);
  if (loading || !data) return <Loading rows={4} />;
  const t = data.totals;
  const tiles = [
    ["Received", t.received], ["Accepted", t.accepted], ["Cancelled", t.cancelled],
    ["Processed", t.processed], ["Delayed", t.delayed], ["Delivered", t.delivered],
    ["Failed", t.failed], ["Avg Order Value", "₹" + inr(t.aov)],
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {tiles.map(([l, v]) => <Stat key={l} label={l} value={typeof v === "number" ? v.toLocaleString("en-IN") : v} />)}
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Stat label="Avg Processing Time" value={`${t.proc_time} min`} />
        <Stat label="Avg Delivery Time" value={`${t.delivery_time} hrs`} />
      </div>
      <Panel title="Orders Received vs Delivered">
        <MultiLine data={data.trend} lines={[{ key: "received", name: "Received" }, { key: "delivered", name: "Delivered" }]} height={300} />
      </Panel>
    </div>
  );
}

function Services({ q, deps }) {
  const { data, loading } = useFetch(`/bi/services${q}`, deps);
  if (loading || !data) return <Loading rows={4} />;
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Revenue by Service"><Bars data={data.services.map((s) => ({ name: s.name, value: s.revenue }))} horizontal currency height={300} /></Panel>
        <Panel title="Orders by Service"><Donut data={data.services.map((s) => ({ name: s.name, value: s.orders }))} height={300} /></Panel>
      </div>
      <Panel title="Service Performance" subtitle="Revenue, growth, margin, repeat & complaint rates">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Service</TableHead><TableHead className="text-right">Orders</TableHead>
              <TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">Growth</TableHead>
              <TableHead className="text-right">Margin</TableHead><TableHead className="text-right">Customers</TableHead>
              <TableHead className="text-right">Repeat</TableHead><TableHead className="text-right">Complaints</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.services.map((s) => (
              <TableRow key={s.name} className="hover:bg-slate-50">
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-right">{s.orders.toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-right">₹{inr(s.revenue)}</TableCell>
                <TableCell className={`text-right font-medium ${s.growth >= 0 ? "text-emerald-600" : "text-red-600"}`}>{s.growth}%</TableCell>
                <TableCell className="text-right">{s.margin}%</TableCell>
                <TableCell className="text-right">{s.customers}</TableCell>
                <TableCell className="text-right">{s.repeat_rate}%</TableCell>
                <TableCell className="text-right">{s.complaint_rate}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>
    </div>
  );
}

function CustomersBI() {
  const { data: seg } = useFetch("/customers/segments", []);
  const { data: ret } = useFetch("/customers/retention", []);
  if (!seg || !ret) return <Loading rows={3} />;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total Customers" value={ret.total} />
        <Stat label="Repeat Customers" value={ret.repeat} tone="text-primary" />
        <Stat label="Retention Rate" value={`${ret.retention_rate}%`} tone="text-emerald-600" />
        <Stat label="Churn Rate" value={`${ret.churn_rate}%`} tone="text-red-600" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Revenue by Segment"><Bars data={seg.map((s) => ({ name: s.segment, value: s.revenue }))} horizontal currency height={320} /></Panel>
        <Panel title="Customers by Segment"><Donut data={seg.map((s) => ({ name: s.segment, value: s.count }))} height={320} /></Panel>
      </div>
    </div>
  );
}

function Growth({ hub }) {
  const { data, loading } = useFetch(`/bi/growth?hub=${hub}`, [hub]);
  if (loading || !data) return <Loading rows={3} />;
  return (
    <div className="space-y-6">
      <Panel title="Monthly Revenue Growth"><AreaTrend data={data.trend.map((m) => ({ date: m.month, value: m.revenue }))} currency height={300} /></Panel>
      <Panel title="Monthly Orders"><Bars data={data.trend.map((m) => ({ name: m.month, value: m.orders }))} height={300} /></Panel>
    </div>
  );
}
